import PropTypes from "prop-types";

import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { strCode, statusNames, userTypeNames } from "../../../../../consts";
import { t } from "../../../../../services";
import { dispatch } from "../../../../../state/store";
import { setUserFilters } from "../../../../../state/ducks";
import { arrayUtil } from "../../../../../utils";

import {
  FilterDropdown,
  FilterInput,
  FlexContainers,
  TextButton,
  WrapperContainer
} from "../../../../../components";
import { FiltersWrapper } from "./styled";
import { FilterCounter, FilterSwitchButton } from "../../styled";

const FiltersTab = ({ visible, setActiveFilters, typeOptions, loading }) => {
  const markets = useSelector((store) => store.markets?.markets) ?? [];
  const appTypes = useSelector((store) => store.configs?.appTypesGroups) ?? [];
  const groups = useSelector((store) => store.userGroups.groups) ?? [];
  const groupsLoading = useSelector((store) => store.userGroups.loading) ?? [];

  const filters = useSelector((store) => store.users?.userFilters);

  const {
    status: selectedStatus,
    apps: selectedApps,
    markets: selectedMarkets,
    groups: selectedGroups,
    type: selectedType
  } = filters;

  const marketOptions = useMemo(() => {
    let options = markets.map((market) => {
      return {
        id: market.market,
        value: market.market
      };
    });
    return options;
  }, [markets.length]);

  const appOptions = useMemo(() => {
    const options = appTypes.map((x) => {
      return {
        value: x.name,
        id: x.appType
      };
    });

    return options;
  }, [appTypes]);

  const groupOptions = useMemo(() => {
    const options = groups.map((x) => {
      return {
        value: x.name,
        id: x.id
      };
    });

    return options;
  }, [groups]);

  const statusOptions = useMemo(
    () => [
      { id: statusNames.allStatuses, value: t(strCode.ALL_STATUSES_TITLE) },
      { id: statusNames.active, value: t(strCode.ACTIVE_STATUSES_TITLE) },
      { id: statusNames.inactive, value: t(strCode.INACTIVE_STATUSES_TITLE) }
    ],
    []
  );

  const initialState = useMemo(() => {
    return {
      status: statusOptions[0].id,
      apps: [],
      markets: [],
      groups: []
    };
  }, [statusOptions, appOptions, marketOptions, groupOptions]);

  const changedState = useMemo(() => {
    return {
      status: selectedStatus !== initialState.status,
      apps: !arrayUtil.arrayEquals(selectedApps ?? [], initialState.apps),
      markets: !arrayUtil.arrayEquals(selectedMarkets ?? [], initialState.markets),
      groups: !arrayUtil.arrayEquals(selectedGroups ?? [], initialState.groups)
    };
  }, [selectedStatus, selectedApps, selectedMarkets, selectedGroups, initialState]);

  useEffect(() => {
    const activeFilters = Object.values(changedState).reduce(
      (acc, value) => (acc += value ? 1 : 0),
      0
    );

    setActiveFilters(activeFilters);
  }, [changedState]);

  const onAppsChanged = (ids) => {
    const selectedAppsOptions = appOptions.filter((x) => ids.includes(x.id)).map((x) => x.id);

    dispatch(
      setUserFilters({
        apps: selectedAppsOptions ?? []
      })
    );
  };

  const onGroupsChanged = (ids) => {
    const selectedGroupOptions = groupOptions.filter((x) => ids.includes(x.id)).map((x) => x.id);

    dispatch(
      setUserFilters({
        groups: selectedGroupOptions ?? []
      })
    );
  };

  const onMarketsChanged = (ids) => {
    const selectedMarketsOptions = marketOptions.filter((x) => ids.includes(x.id)).map((x) => x.id);
    dispatch(
      setUserFilters({
        markets: selectedMarketsOptions ?? []
      })
    );
  };

  const onStatusChanged = (id) => {
    dispatch(setUserFilters({ status: id }));
  };

  const clearFilters = useCallback(() => {
    dispatch(
      setUserFilters({
        apps: initialState.apps,
        groups: initialState.groups,
        markets: initialState.markets,
        status: initialState.status
      })
    );
  }, [initialState]);

  useEffect(() => {
    if (!selectedApps && !selectedGroups && !selectedMarkets && !selectedStatus && !selectedType) {
      clearFilters();
      dispatch(setUserFilters({ type: userTypeNames.all }));
    }
  }, [clearFilters, initialState]);

  return (
    <WrapperContainer Container={FlexContainers.FlexRowStart} width={"100%"} gap={44}>
      <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8}>
        {typeOptions.map((x) => (
          <FilterSwitchButton
            key={x.id}
            onClick={() => dispatch(setUserFilters({ type: x.id }))}
            active={selectedType === x.id}>
            <FilterCounter>{!loading && x.count}</FilterCounter>
            {x.value}
          </FilterSwitchButton>
        ))}
      </WrapperContainer>

      <FiltersWrapper
        Container={FlexContainers.FlexRowStart}
        width={"100%"}
        gap={24}
        visible={visible}>
        <FilterDropdown
          active={changedState.status}
          options={statusOptions}
          optionsListWidth={"100%"}
          onSelectOption={onStatusChanged}
          selectedOption={statusOptions.find((x) => x.id === selectedStatus)}
          InputComponent={<span>{statusOptions.find((x) => x.id === selectedStatus)?.value}</span>}
        />

        {appOptions.length > 1 && (
          <FilterInput
            options={appOptions}
            isActive={changedState.apps}
            selectedOptions={appOptions.filter((x) => selectedApps?.includes(x.id))}
            allOptionsText={strCode.ALL_APPS_TITLE}
            title={t(strCode.APPS_SEARCH_TITLE)}
            description={t(strCode.APPS_SEARCH_DESCRIPTION)}
            searchPlaceholder={t(strCode.APPS_SEARCH_PLACEHOLDER)}
            columnName={t(strCode.APP_NAMES)}
            onSelectedOptionsChanged={onAppsChanged}
          />
        )}

        {marketOptions.length > 1 && (
          <FilterInput
            options={marketOptions}
            isActive={changedState.markets}
            selectedOptions={marketOptions.filter((x) => selectedMarkets?.includes(x.id))}
            allOptionsText={
              marketOptions.length === 1 ? marketOptions[0].value : strCode.ALL_MARKETS_TITLE
            }
            title={t(strCode.MARKETS_SEARCH_TITLE)}
            description={t(strCode.MARKETS_SEARCH_DESCRIPTION)}
            searchPlaceholder={t(strCode.MARKETS_SEARCH_PLACEHOLDER)}
            columnName={t(strCode.MARKET_NAMES)}
            onSelectedOptionsChanged={onMarketsChanged}
          />
        )}

        {(groupOptions.length > 1 || groupsLoading) && (
          <FilterInput
            options={groupOptions}
            isActive={changedState.groups}
            selectedOptions={groupOptions.filter((x) => selectedGroups?.includes(x.id))}
            allOptionsText={
              groupOptions.length === 1 ? groupOptions[0].value : strCode.ALL_GROUPS_TITLE
            }
            title={t(strCode.GROUP_SEARCH_TITLE)}
            description={t(strCode.GROUP_SEARCH_DESCRIPTION)}
            searchPlaceholder={t(strCode.SEARCH_GROUPS_PLACEHOLDER)}
            columnName={t(strCode.GROUP_NAMES)}
            onSelectedOptionsChanged={onGroupsChanged}
          />
        )}

        {(changedState.apps ||
          changedState.groups ||
          changedState.markets ||
          changedState.status) && (
          <TextButton onClick={clearFilters}>{t(strCode.CLEAR_BTN)}</TextButton>
        )}
      </FiltersWrapper>
    </WrapperContainer>
  );
};

FiltersTab.propTypes = {
  visible: PropTypes.bool,
  setActiveFilters: PropTypes.func.isRequired,
  typeOptions: PropTypes.array.isRequired,
  loading: PropTypes.bool
};

export default FiltersTab;
